import React, { PropsWithChildren, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useSession } from 'next-auth/react';
import Footer from '../footer/footer';
import HeaderNavbar from '../header/header';
import { SidebarDesktop } from '../sidebars';
import authUtils from 'utils/auth';
import { headerNavbarOptions } from 'const';
import cmsSidebarNavigation from 'const/sideBarNavigation';
import { NavigationOptions } from 'interfaces';
import { filterNavigationOptionsByRolesOrPermissions } from 'utils/options';
import { Button, Typography } from 'components/common';
import WithAuthorizationComponent from 'components/auth/withAuthorizationComponent';
import { CypressI } from 'interfaces/cypress';
import { usePathname } from 'next/navigation';
import Images from '@/const/images';
import { ChatButton } from '@/components/dashboard';

export interface Layout_Props {
	title?: string;
	classNameTitle?: string;

	withHeader?: boolean;
	customHeader?: React.ReactNode;

	withFooter?: boolean;
	customFooter?: React.ReactNode;

	withSidebar?: boolean;
	customSidebar?: React.ReactNode;

	buttonTitle?: string;
	classNameButton?: string;
	hrefButton?: string;
	onClickButton?: () => void;

	classNameChildren?: string;
	classNameLayout?: string;

	allowedPermissions?: string[];
	allowedRoles?: string[];
}

/**
 * This is the main layout component to render a page
 * It has a default behaviour/style but it also considers when a custom component is meant to be rendered
 *
 * @param props they will handle the behaviour of the layout and the styles
 * @returns JSX element for the layout container
 */
export const Layout: React.FC<PropsWithChildren<Layout_Props & CypressI>> = ({
	dataCY,
	title,
	classNameTitle,

	withHeader = true,
	customHeader = undefined,

	withFooter = false,
	customFooter = undefined,

	withSidebar = true,
	customSidebar = undefined,

	buttonTitle,
	classNameButton = undefined,
	hrefButton,
	onClickButton,

	children,
	classNameChildren,
	classNameLayout,

	allowedPermissions,
	allowedRoles,
}) => {
	const session = useSession();
	const pathname = usePathname();
	const logoUrl = Images.logo;

	// SideBar options
	const [sideBarOptions, setSideBarOptions] = useState<NavigationOptions[]>([]);
	const [showSidebar, setShowSidebar] = React.useState<boolean>(false); // Only mobile screen

	// Set session for AuthUtils
	// This is being called in layout to keep session updated
	React.useEffect(() => {
		if (session) authUtils.setSession(session);
	}, [session]);

	// Filter the sidebar options based on the user role
	useEffect(() => {
		const filteredOptions =
			filterNavigationOptionsByRolesOrPermissions(cmsSidebarNavigation);
		setSideBarOptions(filteredOptions);
	}, [session]);

	// Show chat button only for clients in specific paths
	const shouldShowChatButton = (): boolean => {
		if (!session) return false;

		const allowedPaths = [
			'/clientapp/all-cases',
			'/cases',
			'/file-your-claim/',
		];

		const isClient = session.data?.user?.user.role === 'client';
		const isAllowedPath = allowedPaths.some((path) =>
			pathname?.startsWith(path)
		);

		return isClient && isAllowedPath;
	};

	return (
		<div
			data-cy={`${dataCY}-layout`}
			className={clsx('w-full relative', classNameLayout)}
		>
			{/* Header */}
			{withHeader && !customHeader && (
				<HeaderNavbar
					dataCY={`${dataCY}-header`}
					navBarOptions={headerNavbarOptions}
					logoUrl={logoUrl}
					showSidebar={showSidebar}
					setShowSidebar={setShowSidebar}
				/>
			)}
			{withHeader && customHeader && <>{customHeader}</>}

			{/* Content of the layout */}
			<div
				className={clsx('flex min-h-screen w-full', {
					'lg:pt-24': customHeader,
				})}
			>
				{/* Sidebar */}
				{withSidebar && !customSidebar && (
					<SidebarDesktop
						role="client"
						dataCY={`${dataCY}-sidebar`}
						itemOptions={sideBarOptions}
						showSidebar={showSidebar}
						logoUrl={logoUrl}
					/>
				)}
				{withSidebar && customSidebar && <>{customSidebar}</>}

				{/* Right container for actual page content */}
				<div
					className={clsx('w-full', {
						'relative w-full mb-10 lg:overflow-y-auto scroll-custom':
							withSidebar,
					})}
				>
					{/* Content - Children */}
					<main
						className={clsx(
							'w-full  md:pt-0 md:mt-5 lg:mt-0 bg-[#FBFBFB]',
							// pathname?.startsWith('/cases/') ||
							// 	pathname?.startsWith('/clientapp/all-cases/')
							// 	? 'bg-[#F4F4F9]'
							// 	: 'bg-gradient-to-b from-[#FFFFFF] to-[#F4F4F9]',

							{
								'relative w-full px-9 md:px-16 py-10 mb-10': withSidebar,
								'-z-10': showSidebar,
							},
							classNameChildren
						)}
					>
						<div className="flex gap-x-2 justify-between items-center">
							{title && (
								<Typography
									type="custom-h1"
									text={title}
									className={clsx(
										'text-md lg:text-xl font-bold text-gray-800',
										classNameTitle
									)}
								/>
							)}
							{buttonTitle && (
								<WithAuthorizationComponent
									allowedPermissions={allowedPermissions}
									allowedRoles={allowedRoles}
								>
									<Button
										label={buttonTitle}
										decoration="primary"
										size="fit"
										className={classNameButton}
										onClick={onClickButton}
										href={hrefButton}
									/>
								</WithAuthorizationComponent>
							)}
						</div>

						{children}

						{shouldShowChatButton() && <ChatButton />}
					</main>

					{/* Footer */}
					{withFooter && !customFooter && (
						<Footer dataCY={`${dataCY}-footer`} />
					)}
					{withFooter && customFooter && <>{customFooter}</>}
				</div>
			</div>
		</div>
	);
};
